import {createReducer, on} from "@ngrx/store";
import {
  ClearPhoneNumber,
  ClearState,
  FetchConsentFormLinkFailure,
  FetchConsentFormLinkRequest,
  FetchConsentFormLinkSuccess,
  FetchObQuestionsFailure,
  FetchObQuestionsRequest,
  FetchObQuestionsSuccess,
  ForgotPasswordFailure,
  ForgotPasswordRequest,
  ForgotPasswordSuccess,
  GenerateOtpFailure,
  GenerateOtpRequest,
  GenerateOtpSuccess,
  LoginFailure,
  LoginRequest,
  LoginSuccess,
  LogoutFailure,
  LogoutRequest,
  LogoutSuccess,
  RegisterFailure,
  RegisterRequest,
  RegisterSuccess,
  SaveAnswersFailure,
  SaveAnswersRequest,
  SaveAnswersSuccess,
  SetHasMedicalProblemStatus,
  SetHasMedicationStatus,
  SetRole,
  StartRegistrationLoading,
  StopRegistrationLoading,
  VerifyOtpFailure,
  VerifyOtpForTfaFailure,
  VerifyOtpForTfaRequest,
  VerifyOtpForTfaSuccess,
  VerifyOtpRequest,
  VerifyOtpSuccess,
  VerifyUsernameFailure,
  VerifyUsernameRequest,
  VerifyUsernameSuccess,
} from "./authentication.actions";
import {QuestionList} from "../../../models/question-list";

// State for this feature (User)
export interface AuthenticationState {
  registrationInfo: {
    loading: boolean
  };
  loginInfo: {
    loading: boolean
  };
  logoutInfo: {
    loading: boolean
  };
  updateAccount: {
    loading: boolean
  };
  forgotPasswordInfo: {
    loading: boolean
    enableResend: boolean,
  };
  phoneNumber: string;
  hasMedicalProblems: boolean;
  hasMedication: boolean;
  consentFormLink: string;
  questionList: QuestionList;
  role: number;
  isUsernameAvailableInfo: {
    isUsernameAvailable: boolean,
    loading: boolean
  };
  generateOtpInfo: {
    loading: boolean,
  };
  verifyOtpInfo: {
    loading: boolean,
  },
  saveAnswersInfo: {
    loading: boolean,
  },
  verifyOtpForTfaInfo: {
    loading: boolean,
  }
}

const initialState: AuthenticationState = {
  registrationInfo: {
    loading: false
  },
  loginInfo: {
    loading: false
  },
  logoutInfo: {
    loading: false
  },
  updateAccount: {
    loading: false
  },
  forgotPasswordInfo: {
    loading: false,
    enableResend: false,
  },
  phoneNumber: '',
  hasMedicalProblems: false,
  hasMedication: false,
  consentFormLink: '',
  role: null,
  isUsernameAvailableInfo: {
    isUsernameAvailable: false,
    loading: false
  },
  questionList: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  generateOtpInfo: {
    loading: false,
  },
  verifyOtpInfo: {
    loading: false,
  },
  saveAnswersInfo: {
    loading: false,
  },
  verifyOtpForTfaInfo: {
    loading: false,
  }
};

export const reducer = createReducer(
  initialState,

  // CLEAR STATE
  on(ClearState, (state) => initialState),

  // CLEAR PHONE NUMBER
  on(ClearPhoneNumber, (state) => ({
    ...state,
    phoneNumber: ''
  })),

  // LOGIN
  on(LoginRequest, (state) => ({
    ...state,
    loginInfo: {
      loading: true
    }
  })),
  on(LoginSuccess, (state) => ({
    ...state,
    loginInfo: {
      loading: false
    }
  })),
  on(LoginFailure, (state) => ({
    ...state,
    loginInfo: {
      loading: false
    }
  })),

  // REGISTER
  on(RegisterRequest, (state) => ({
    ...state,
    registrationInfo: {
      loading: true
    }
  })),
  on(RegisterSuccess, (state) => ({
    ...state,
    registrationInfo: {
      loading: false
    }
  })),
  on(RegisterFailure, (state) => ({
    ...state,
    registrationInfo: {
      loading: false
    }
  })),

  // LOGOUT
  on(LogoutRequest, (state) => ({
    ...state,
    logoutInfo: {
      loading: true
    }
  })),
  on(LogoutSuccess, (state) => ({
    ...state,
    logoutInfo: {
      loading: false
    }
  })),
  on(LogoutFailure, (state) => ({
    ...state,
    logoutInfo: {
      loading: false
    }
  })),

  // FORGOT PASSWORD
  on(ForgotPasswordRequest, (state) => ({
    ...state,
    forgotPasswordInfo: {
      loading: true,
      enableResend: false
    }
  })),
  on(ForgotPasswordSuccess, (state) => ({
    ...state,
    forgotPasswordInfo: {
      loading: false,
      enableResend: true
    }
  })),
  on(ForgotPasswordFailure, (state) => ({
    ...state,
    forgotPasswordInfo: {
      loading: false,
      enableResend: true
    }
  })),

  // GENERATE OTP
  on(GenerateOtpRequest, (state, props) => ({
    ...state,
    phoneNumber: props.payload.phoneNumber,
    generateOtpInfo: {
      loading: true,
    }
  })),
  on(GenerateOtpSuccess, (state, props) => ({
    ...state,
    phoneNumber: props.phoneNumber,
    generateOtpInfo: {
      loading: false,
    }

  })),
  on(GenerateOtpFailure, (state) => ({
    ...state,
    generateOtpInfo: {
      loading: false,
    }
  })),

  //SET HAS MEDICATION STATUS
  on(SetHasMedicationStatus, (state, props) => ({
    ...state,
    hasMedication: props.hasMedication
  })),

  //SET HAS MEDICAL PROBLEMS STATUS
  on(SetHasMedicalProblemStatus, (state, props) => ({
    ...state,
    hasMedicalProblems: props.hasMedicalProblems
  })),

  // FETCH CONSENT FORM LINK
  on(FetchConsentFormLinkRequest, (state) => ({
    ...state,
  })),
  on(FetchConsentFormLinkSuccess, (state, props) => ({
    ...state,
    consentFormLink: props.consentFormLink
  })),
  on(FetchConsentFormLinkFailure, (state) => ({
    ...state,
    consentFormLink: state.consentFormLink
  })),

  //SET ROLE
  on(SetRole, (state, props) => ({
    ...state,
    role: props.role
  })),

  // VERIFY USERNAME
  on(VerifyUsernameRequest, (state) => ({
    ...state,
    isUsernameAvailableInfo: {
      isUsernameAvailable: initialState.isUsernameAvailableInfo.isUsernameAvailable,
      loading: true
    }
  })),
  on(VerifyUsernameSuccess, (state, props) => ({
    ...state,
    isUsernameAvailableInfo: {
      isUsernameAvailable: props.isAvailable,
      loading: false
    }
  })),
  on(VerifyUsernameFailure, (state) => ({
    ...state,
    isUsernameAvailableInfo: {
      isUsernameAvailable: initialState.isUsernameAvailableInfo.isUsernameAvailable,
      loading: false
    }
  })),

  // FETCH OB QUESTIONS
  on(FetchObQuestionsRequest, (state) => ({
    ...state,
    questionList: {
      ...initialState.questionList,
      loading: true
    }
  })),
  on(FetchObQuestionsSuccess, (state, props) => ({
    ...state,
    questionList: {
      ...props.questionList,
      loading: false
    }
  })),
  on(FetchObQuestionsFailure, (state) => ({
    ...state,
    questionList: {
      ...initialState.questionList,
      loading: false
    }
  })),

  // VERIFY OTP
  on(VerifyOtpRequest, (state) => ({
    ...state,
    verifyOtpInfo: {
      loading: true
    }
  })),
  on(VerifyOtpSuccess, (state) => ({
    ...state,
    verifyOtpInfo: {
      loading: false
    }
  })),
  on(VerifyOtpFailure, (state) => ({
    ...state,
    verifyOtpInfo: {
      loading: false
    }
  })),

  //START REGISTRATION LOADING
  on(StartRegistrationLoading, (state) => ({
    ...state,
    registrationInfo: {
      loading: true
    },
    generateOtpInfo: {
      loading: true
    },
    verifyOtpInfo: {
      loading: true
    },
  })),

  //STOP REGISTRATION LOADING
  on(StopRegistrationLoading, (state) => ({
    ...state,
    registrationInfo: {
      loading: false
    },
    generateOtpInfo: {
      loading: false
    },
    verifyOtpInfo: {
      loading: false
    },
  })),

  // SAVE ANSWERS
  on(SaveAnswersRequest, (state) => ({
    ...state,
    saveAnswersInfo: {
      loading: true,
    }
  })),
  on(SaveAnswersSuccess, (state) => ({
    ...state,
    saveAnswersInfo: {
      loading: false,
    }
  })),
  on(SaveAnswersFailure, (state) => ({
    ...state,
    saveAnswersInfo: {
      loading: false,
    }
  })),

  // VERIFY OTP FOR TFA
  on(VerifyOtpForTfaRequest, (state) => ({
    ...state,
    verifyOtpForTfaInfo: {
      loading: true
    }
  })),
  on(VerifyOtpForTfaSuccess, (state) => ({
    ...state,
    verifyOtpForTfaInfo: {
      loading: false
    }
  })),
  on(VerifyOtpForTfaFailure, (state) => ({
    ...state,
    verifyOtpForTfaInfo: {
      loading: false
    }
  })),
);
